// ─── Progress photo render URLs ───
// Progress photos are no longer readable from a permanent public URL. The API hands out
// signed URLs that expire within the hour, so a render URL is NOT a durable handle:
// persisting one would sync a link that is dead in 60 minutes and would write the bucket's
// object key into durable state. The durable handle is the photo id; the URL is resolved at
// display time and held in memory only (see src/utils/photoSign.js for the React side).
//
// Resolution order for one photo — local first, permanent URL last:
//   1. local   — base64 from the camera (`data:`) or a blob: URL from this session: no network
//   2. signed  — a fresh signed URL for the photo id
//   3. legacy  — a `public_url` (or an http url stored in `data`) from before photos were signed;
//                kept only so nothing breaks the day the bucket's public origin is switched off
//   4. pending / unavailable — show a placeholder, never a broken image
//
// This file is pure and import-free so tests/photo-urls.test.mjs can load it directly.

export const RENEW_SKEW_MS = 5 * 60 * 1000;   // re-sign this long before the URL actually expires
export const URL_TTL_MS = 60 * 60 * 1000;     // assumed lifetime when the API omits expires_in
export const FAIL_BACKOFF_MS = 30 * 1000;     // after a failed/unsigned id, wait before asking again
export const MAX_BATCH = 200;                 // the API's per-call photoIds limit

// The id the API signs against: server rows carry `photo_id`, local entries carry `id`.
export const photoKey = (p) => (p && (p.photo_id || p.id)) || null;

const str = (v) => (typeof v === 'string' ? v : '');

// A source that needs no network: camera base64, or a blob: URL created this session.
export function localSrc(p) {
  for (const v of [str(p && p.data), str(p && p.photo_data)]) {
    if (v.startsWith('data:') || v.startsWith('blob:')) return v;
  }
  return '';
}

// A permanent URL stored before photos were signed. Last resort only.
export function legacySrc(p) {
  const d = str(p && p.data);
  if (/^https?:/.test(d)) return d;
  const pub = str(p && p.public_url);
  return /^https?:/.test(pub) ? pub : '';
}

// A cache entry is { url, expiresAt } plus optional { inflight, failedAt }.
export const isFresh = (e, now) => !!(e && e.url && e.expiresAt - now > RENEW_SKEW_MS);

// Which photo ids on screen need signing right now: no fresh URL, not already in flight,
// and past the failure backoff. Photos that render locally are never signed.
export function idsToSign(photos, cache, now) {
  const out = [];
  const seen = new Set();
  for (const p of Array.isArray(photos) ? photos : []) {
    if (!p || localSrc(p)) continue;
    const id = photoKey(p);
    if (!id || seen.has(id)) continue;
    const e = (cache || {})[id];
    if (isFresh(e, now) || (e && e.inflight)) continue;
    if (e && e.failedAt && now - e.failedAt < FAIL_BACKOFF_MS) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= MAX_BATCH) break;
  }
  return out;
}

export function markInflight(cache, ids, now) {
  const next = { ...(cache || {}) };
  for (const id of ids || []) next[id] = { ...(next[id] || {}), inflight: true, inflightAt: now };
  return next;
}

// One signed URL into the cache (also used for the URL the upload response already carries).
export function putSigned(cache, id, url, expiresIn, now) {
  if (!id || !url) return cache || {};
  const ttl = Number(expiresIn) > 0 ? Number(expiresIn) * 1000 : URL_TTL_MS;
  return { ...(cache || {}), [id]: { url, expiresAt: now + ttl } };
}

// The `{ urls, expires_in }` body of a signed_urls response. Ids the server did not sign are
// left for markFailed, so a photo with no stored object is not requested on every render.
export function mergeSigned(cache, urls, expiresIn, now) {
  let next = { ...(cache || {}) };
  for (const [id, url] of Object.entries(urls || {})) {
    if (typeof url === 'string' && url) next = putSigned(next, id, url, expiresIn, now);
  }
  return next;
}

export function markFailed(cache, ids, now) {
  const next = { ...(cache || {}) };
  for (const id of ids || []) {
    const e = next[id] || {};
    // Keep a URL that is merely stale — it may still be the best we can show
    next[id] = { ...e, inflight: false, failedAt: now };
  }
  return next;
}

// What to render for one photo. `state` lets the UI show an honest placeholder instead of a
// broken image: 'pending' while a signature is on its way, 'unavailable' when there is nothing.
export function photoSrc(p, cache, now) {
  const local = localSrc(p);
  if (local) return { src: local, state: 'local' };
  const id = photoKey(p);
  const e = id ? (cache || {})[id] : null;
  if (isFresh(e, now)) return { src: e.url, state: 'signed' };
  const legacy = legacySrc(p);
  if (legacy) return { src: legacy, state: 'legacy' };
  if (e && e.url) return { src: e.url, state: 'signed' }; // stale but the only thing we have
  if (!id) return { src: '', state: 'unavailable' };
  if (e && e.failedAt && !e.inflight) return { src: '', state: 'unavailable' };
  return { src: '', state: 'pending' };
}
